import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { PhoneCall, Brain, CalendarCheck, MessageCircle } from "lucide-react";

const content = {
  en: {
    overline: "How It Works",
    title: "From ringing phone to confirmed booking",
    sub: "The technical story, told in plain English.",
    steps: [
      { icon: PhoneCall, h: "1. The call comes in", p: "A patient calls your clinic at any hour. Sawtly answers on the first ring — in Najdi, Hijazi, or Gulf English, matched to your city. No hold music, no voicemail." },
      { icon: Brain, h: "2. The AI understands", p: "Speech recognition tuned for Gulf dialects transcribes the caller in real time — names, numbers, and code-switching included. The dialogue brain knows your services, prices, and calendar rules, and it confirms every detail back before booking." },
      { icon: CalendarCheck, h: "3. The slot gets booked", p: "The appointment lands directly in your calendar inside the rules you set: services, durations, blocked hours, buffers. Emergencies transfer instantly to your on-call number." },
      { icon: MessageCircle, h: "4. WhatsApp confirms it", p: "Caller and clinic both get a WhatsApp confirmation. Reminders go out before the appointment. You watch recovered bookings — and recovered revenue — in your dashboard." },
    ],
  },
  ar: {
    overline: "كيف يعمل",
    title: "من جرس الجوال إلى حجز مؤكد",
    sub: "القصة التقنية، بلغة بسيطة.",
    steps: [
      { icon: PhoneCall, h: "1. المكالمة توصل", p: "مريض يتصل على عيادتك في أي ساعة. صوتلي يرد من أول رنة — بالنجدي أو الحجازي أو الإنجليزي الخليجي، مطابق لمدينتك. بدون موسيقى انتظار ولا بريد صوتي." },
      { icon: Brain, h: "2. الذكاء يفهم", p: "التعرف الصوتي المضبوط للهجات الخليجية يكتب المتصل لحظيًا — أسماء وأرقام وتبديل بين اللغتين. عقل الحوار يعرف خدماتك وأسعارك وقواعد تقويمك، ويأكد كل تفصيلة قبل الحجز." },
      { icon: CalendarCheck, h: "3. الموعد ينحجز", p: "الموعد يوصل مباشرة لتقويمك داخل القواعد اللي حطيتها: الخدمات، المدد، الساعات المغلقة، الفواصل. الطوارئ تتحول فورًا لرقم المناوبة." },
      { icon: MessageCircle, h: "4. واتساب يأكد", p: "المتصل والعيادة يستلمون تأكيد واتساب. التذكيرات توصل قبل الموعد. وأنت تتابع الحجوزات المسترجعة — والإيراد المسترجع — في لوحتك." },
    ],
  },
};

const HowItWorks = () => {
  const { lang } = useLanguage();
  const c = content[lang];
  return (
    <main data-testid="how-it-works-page" className="mx-auto max-w-7xl px-6 pb-24 pt-36 lg:px-10 lg:pt-44">
      <Reveal>
        <p className="overline-tag">{c.overline}</p>
        <h1 className="mt-4 font-display text-4xl font-extrabold md:text-6xl">{c.title}</h1>
        <p className="mt-4 max-w-xl text-sm text-zinc-400 md:text-base">{c.sub}</p>
      </Reveal>
      <div className="mt-16 grid gap-8 md:grid-cols-2">
        {c.steps.map((s, i) => (
          <Reveal key={i} delay={i * 0.1} className={i % 3 === 0 ? "md:col-span-2 lg:col-span-1" : ""}>
            <div data-testid={`hiw-step-${i}`} className="group h-full rounded-3xl border border-white/10 bg-surface p-10 transition-[border-color,transform] duration-500 hover:-translate-y-2 hover:border-neon/40">
              <s.icon size={32} className="text-neon" />
              <h2 className="mt-6 font-display text-xl font-bold md:text-2xl">{s.h}</h2>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400 md:text-base">{s.p}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </main>
  );
};

export default HowItWorks;
